import introJs from 'intro.js';
import React, { ReactElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { waitForElement } from './utils/waitForElement';
import 'intro.js/introjs.css';
import { tabKeyEventListener } from './utils/tabKeyEventListener';

export enum Position {
  top = 'top',
  left = 'left',
  right = 'right',
  bottom = 'bottom',
  bottomLeftAligned = 'bottom-left-aligned',
  bottomMiddleAligned = 'bottom-middle-aligned',
  bottomRightAligned = 'bottom-right-aligned',
  auto = 'auto',
}

export type Step = {
  userGuideId?: string;
  content: ReactElement<string | JSX.Element> | string;
  position: Position;
  onBefore?: () => void;
  onAfter?: () => void;
  onActive?: () => void;
  contentText?: string;
  isMediaContent?: boolean;
};

export type UserGuideConfig = {
  onExit: () => void;
  steps: Step[];
};

const MEDIA_CONTENT_MAX_WIDTH = 600;

export class UserGuide {
  private intro: introJs.IntroJs;
  private tabKeyEventListener: (e: KeyboardEvent) => void;
  steps: Step[];

  constructor({ onExit, steps }: UserGuideConfig) {
    this.intro = introJs();

    const onExitCallback = () => {
      const currentStepItem = this.intro['_introItems'][this.intro['_currentStep']];

      if (currentStepItem && 'onAfter' in currentStepItem) {
        currentStepItem.onAfter();
      }

      onExit();
    };

    this.intro.onexit(() => {
      window.removeEventListener('keydown', this.tabKeyEventListener);
      onExitCallback();
    });
    this.intro.oncomplete(() => {
      window.removeEventListener('keydown', this.tabKeyEventListener);
      onExitCallback();
    });

    this.steps = steps;
    const stepsToAdd = steps.map(step => {
      const { contentText = '', content, isMediaContent = false, ...rest } = step;

      const elementToBeRendered = !isMediaContent ? (
        content
      ) : (
        <>
          <div style={{ maxWidth: MEDIA_CONTENT_MAX_WIDTH, display: 'flex' }}>{content}</div>
          <p className="contentText">{contentText}</p>
        </>
      );

      return {
        ...rest,
        intro: renderToStaticMarkup(elementToBeRendered as ReactElement),
        element: step.userGuideId && `[data-user-guide-id="${step.userGuideId}"]`,
        tooltipClass: isMediaContent && 'tooltipDesignClass withImg',
      };
    });

    this.intro.setOptions({
      steps: stepsToAdd,
      tooltipClass: 'tooltipDesignClass',
      disableInteraction: true,
      showBullets: false,
      showButtons: true,
      showStepNumbers: true,
      showProgress: false,
      highlightClass: 'highlightClass',
      buttonClass:
        'button rounded h-10 px-4 font-bold transition-colors inline-flex flex-nowrap items-center justify-between gap-x-2 border-current text-white bg-brand hover:bg-brand-dark no-underline',
    });
  }

  makeTabNavigation = async () => {
    const prevButton = (await waitForElement('.introjs-prevbutton', false)) as HTMLElement;
    const nextButton = (await waitForElement('.introjs-nextbutton', false)) as HTMLElement;
    const closeButton = (await waitForElement('.introjs-skipbutton', false)) as HTMLElement;

    const tabElements = [prevButton, nextButton, closeButton];

    nextButton.focus();

    this.tabKeyEventListener = tabKeyEventListener(tabElements);

    window.addEventListener('keydown', this.tabKeyEventListener);
  };

  start() {
    this.makeTabNavigation();

    this.intro.onbeforechange(async () => {
      return new Promise(resolve => {
        const currentStepItem = this.intro['_introItems'][this.intro['_currentStep']];
        const previousStepItem = this.intro['_introItems'][this.intro['_currentStep'] - 1];

        if (previousStepItem && 'onAfter' in previousStepItem) {
          previousStepItem.onAfter();
        }

        if (currentStepItem && 'onBefore' in currentStepItem && 'userGuideId' in currentStepItem) {
          currentStepItem.onBefore();
          waitForElement(this.steps[this.intro['_currentStep']].userGuideId).then(() => {
            this.intro['_introItems'][this.intro['_currentStep']].element = document.querySelector(
              `[data-user-guide-id="${this.steps[this.intro['_currentStep']]?.userGuideId}"]`
            );
            this.intro['_introItems'][this.intro['_currentStep']].position =
              this.steps[this.intro['_currentStep']].position;

            return setInterval(resolve, 500);
          });
        }

        resolve(null);
      });
    });

    this.intro.onchange(() => {
      const currentStepItem = this.intro['_introItems'][this.intro['_currentStep']];

      if (currentStepItem && 'onActive' in currentStepItem) {
        currentStepItem.onActive();
      }
    });

    this.intro.start();
  }
}
